import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { getSystemHealth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Activity,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

/**
 * Panel de Diagnóstico general (solo admin).
 *
 * GET /api/diagnostics/health ejecuta varias "suites" (motor de cálculo, flujo de
 * caja, exportadores, proyección, API/rutas, seguridad, configuración, base de
 * datos) y devuelve {ok, suites[], resumen}. Cada suite trae checks verde/rojo.
 */
export default function DiagnosticoPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSystemHealth();
      setData(res.data);
      setForbidden(false);
      setLastRun(new Date());
      if (res.data.ok) toast.success("Estado de la app: TODO VERDE");
      else toast.error("Estado de la app: hay comprobaciones en ROJO");
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else {
        toast.error("No se pudo ejecutar el diagnóstico");
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (forbidden) {
    return (
      <div className="max-w-2xl mx-auto mt-12" data-testid="diagnostico-forbidden">
        <Card className="border-rose-500/50">
          <CardContent className="flex items-center gap-4 py-8">
            <ShieldAlert className="h-10 w-10 text-rose-600 shrink-0" />
            <div>
              <div className="text-lg font-bold">Acceso restringido</div>
              <p className="text-sm text-muted-foreground">
                El panel de diagnóstico es una herramienta interna disponible solo
                para administradores.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ok = data?.ok;
  const suites = data?.suites || [];
  const resumen = data?.resumen || {};

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="diagnostico-page">
      {/* Cabecera */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            Estado de la aplicación
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comprobaciones automáticas de cada proceso. Úsalo tras cada cambio para
            ver de un vistazo que no se ha roto nada.
          </p>
        </div>
        <Button onClick={load} disabled={loading} data-testid="rerun-health">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Ejecutando..." : "Re-ejecutar"}
        </Button>
      </div>

      {/* Veredicto global */}
      <Card
        className={
          ok === undefined
            ? ""
            : ok
            ? "border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30"
            : "border-rose-500/50 bg-rose-50 dark:bg-rose-950/30"
        }
        data-testid="health-verdict"
      >
        <CardContent className="flex items-center gap-4 py-6">
          {ok ? (
            <ShieldCheck className="h-10 w-10 text-emerald-600" />
          ) : (
            <ShieldAlert className="h-10 w-10 text-rose-600" />
          )}
          <div className="flex-1">
            <div className="text-xl font-bold">
              {loading
                ? "Ejecutando diagnóstico..."
                : ok
                ? "TODO VERDE — seguro continuar"
                : "HAY FALLOS — revisar antes de seguir"}
            </div>
            <div className="text-sm text-muted-foreground">
              {resumen.ok ?? 0}/{resumen.checks ?? 0} comprobaciones · {suites.length} procesos
              {lastRun && ` · ${lastRun.toLocaleTimeString()}`}
            </div>
          </div>
          <Badge variant={ok ? "default" : "destructive"} className="text-sm">
            {ok ? "OK" : `${resumen.fallidos ?? 0} fallo(s)`}
          </Badge>
        </CardContent>
      </Card>

      {/* Suites */}
      <div className="space-y-3">
        {suites.map((suite) => (
          <SuiteCard key={suite.key} suite={suite} />
        ))}
        {suites.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground text-center py-6">
            Sin datos de diagnóstico.
          </p>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        CLI equivalente del motor de cálculo:{" "}
        <code className="font-mono">python3 scripts/ebitda_health.py</code>
      </p>
    </div>
  );
}

function SuiteCard({ suite }) {
  const [open, setOpen] = useState(!suite.ok); // abre las que fallan
  const passed = suite.checks.filter((c) => c.ok).length;

  return (
    <Card
      className={
        suite.ok ? "border-emerald-500/30" : "border-rose-500/50"
      }
      data-testid={`suite-${suite.key}`}
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer flex-row items-center justify-between py-4">
            <div className="flex items-center gap-3">
              {suite.ok ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 text-rose-600 shrink-0" />
              )}
              <div>
                <CardTitle>{suite.label}</CardTitle>
                <CardDescription className="text-xs">
                  {passed}/{suite.checks.length} comprobaciones
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={suite.ok ? "secondary" : "destructive"}>
                {suite.ok ? "OK" : "FALLO"}
              </Badge>
              {open ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2 pt-0">
            {suite.checks.map((c, i) => (
              <div
                key={i}
                className="flex items-start gap-3 py-2 px-3 rounded-lg border"
              >
                {c.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium">{c.name}</div>
                  {c.detail && (
                    <div className="text-xs text-muted-foreground font-mono mt-0.5 break-all">
                      {c.detail}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
